import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setSearchQuery, setSelectedCategory, setSelectedGameId, fetchGames } from '../redux/slice/game.slice';
import { addToCart, toggleWishlist } from '../redux/slice/user.slice';
import { fetchAllCategories } from '../redux/slice/category.slice';
import { Star, Grid, List, SlidersHorizontal, Search, X, ChevronLeft, ChevronRight, ChevronDown, Check, Loader2 } from 'lucide-react';
import { FaWindows, FaApple, FaXbox } from 'react-icons/fa';
import { DiAndroid } from 'react-icons/di';
import { SiOculus, SiPlaystation, SiSteamdeck } from 'react-icons/si';
import { BsNintendoSwitch } from 'react-icons/bs';
import { TbDeviceVisionProFilled } from 'react-icons/tb';
import gsap from 'gsap';
import { useNavigate, useLocation } from 'react-router-dom';
// import { getGamePath } from '../utils/gamePath';
import ProgressiveImage from '../components/common/ProgressiveImage';
// import LazyImage from '../components/common/LazyImage';
import GameCard, { GameCardSkeleton, GameSkeleton } from '../components/common/GameCard';
import GameListCard, { GameListCardSkeleton } from '../components/common/GameListCard';
import { Helmet } from 'react-helmet-async';
import { restoreScrollPosition } from '../utils/scrollRestoration';
import useGridCorners from '../hooks/useGridCorners';

const PlatformIcon = {
    windows: FaWindows,
    playstation: SiPlaystation,
    xbox: FaXbox,
    nintendo: BsNintendoSwitch,
    quest: SiOculus,
    android: DiAndroid,
    ios: FaApple,
    visionpro: TbDeviceVisionProFilled,
    steamdeck: SiSteamdeck
};

const PlatformColor = {
    windows: '#0078d7',
    playstation: '#00439c',
    xbox: '#107c10',
    nintendo: '#e60012',
    quest: '#ffffff',
    android: '#3ddc84',
    ios: '#a3aaae',
    visionpro: '#a855f7',
    steamdeck: '#38bdf8'
};

const PlatformLabel = {
    windows: 'Windows PC',
    playstation: 'PlayStation',
    xbox: 'Xbox',
    nintendo: 'Nintendo Switch',
    quest: 'Meta Quest VR',
    android: 'Android',
    ios: 'iOS',
    visionpro: 'Vision Pro',
    steamdeck: 'Steam Deck'
};


const CategorySkeleton = () => (
    <div className="flex flex-col items-center flex-shrink-0 animate-pulse select-none">
        <div className="p-[2px] rounded-2xl bg-white/10">
            <div className="w-20 h-20 xs:w-24 xs:h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-[14px] bg-[#0c0c1e]/40 border border-white/5 overflow-hidden flex items-center justify-center">
                <div className="w-full h-full bg-white/[0.03]" />
            </div>
        </div>
        <div className="h-3 w-14 sm:w-16 bg-white/10 rounded mt-2.5" />
    </div>
);

export default function AllGames() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { games, searchQuery, selectedCategory, pagination, loading } = useSelector((s) => s.game);
    const { wishlist } = useSelector((s) => s.userSlice);
    const { categories, loading: categoryLoading } = useSelector((s) => s.category);

    const [viewMode, setViewMode] = useState('grid');
    const [sortBy, setSortBy] = useState('newest'); // Default set to newest (Newest Arrivals)
    const [showFilters, setShowFilters] = useState(false);
    const [isInitialFetched, setIsInitialFetched] = useState(() => Boolean(games && games.length > 0));
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [priceRange, setPriceRange] = useState([0, 150]); // Price range set to max by default
    const [minRating, setMinRating] = useState(0);
    const [selectedGenres, setSelectedGenres] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const categoryParam = params.get('category');
        return categoryParam && categoryParam !== 'All' ? [categoryParam] : [];
    });
    const [selectedPlatforms, setSelectedPlatforms] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const platformParam = params.get('platform');
        return platformParam ? [platformParam] : [];
    });
    const [selectedYears, setSelectedYears] = useState([]);

    const [platformSelectorGame, setPlatformSelectorGame] = useState(null);
    const [selectedPlatformForGame, setSelectedPlatformForGame] = useState('');

    const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery || '');
    const [isSortOpen, setIsSortOpen] = useState(false);
    const dropdownRef = useRef(null);
    const filterBtnRef = useRef(null);
    const sortDropdownRef = useRef(null);
    const categoryScrollRef = useRef(null);
    const [canScrollCategoriesLeft, setCanScrollCategoriesLeft] = useState(false);
    const [canScrollCategoriesRight, setCanScrollCategoriesRight] = useState(false);

    const SORT_OPTIONS = useMemo(() => [
        { value: 'newest', label: 'DEFAULT' },
        { value: 'featured', label: 'FEATURED' },
        { value: 'discount', label: 'TOP DISCOUNTS' },
        { value: 'price-low', label: 'PRICE: ASCENDING' },
        { value: 'price-high', label: 'PRICE: DESCENDING' },
        { value: 'rating', label: 'TOP RATED' }
    ], []);

    const currentSortLabel = useMemo(() => {
        return SORT_OPTIONS.find(s => s.value === sortBy)?.label || 'DEFAULT';
    }, [SORT_OPTIONS, sortBy]);

    useEffect(() => {
        dispatch(fetchAllCategories());
    }, [dispatch]);

    const updateCategoryScrollButtons = useCallback(() => {
        const el = categoryScrollRef.current;
        if (el) {
            const canLeft = el.scrollLeft > 2;
            const canRight = Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth - 2;
            setCanScrollCategoriesLeft(canLeft);
            setCanScrollCategoriesRight(canRight);
        }
    }, []);

    const scrollCategories = (direction) => {
        if (categoryScrollRef.current) {
            const scrollAmount = direction === 'left' ? -350 : 350;
            categoryScrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };

    const categoryList = useMemo(() => {
        return (categories || []).filter(c => c && c.isActive !== false);
    }, [categories]);

    useEffect(() => {
        const el = categoryScrollRef.current;
        if (!el) return;

        updateCategoryScrollButtons();
        el.addEventListener('scroll', updateCategoryScrollButtons, { passive: true });
        window.addEventListener('resize', updateCategoryScrollButtons, { passive: true });

        let resizeObserver;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
                updateCategoryScrollButtons();
            });
            resizeObserver.observe(el);
        }

        const timer = setTimeout(updateCategoryScrollButtons, 100);

        return () => {
            clearTimeout(timer);
            el.removeEventListener('scroll', updateCategoryScrollButtons);
            window.removeEventListener('resize', updateCategoryScrollButtons);
            if (resizeObserver) resizeObserver.disconnect();
        };
    }, [updateCategoryScrollButtons, categoryList]);

    const getCategoryCover = useCallback((cat) => {
        if (!cat) return { url: '', lowUrl: '' };

        let high = '';
        let low = '';

        if (typeof cat.image === 'string' && cat.image) {
            high = cat.image;
            low = cat.image;
        } else if (cat.image?.url) {
            high = cat.image.url;
            low = cat.image.lowUrl || cat.image.url;
        } else if (typeof cat.coverImage === 'string' && cat.coverImage) {
            high = cat.coverImage;
            low = cat.coverImage;
        } else if (cat.cover_image?.url) {
            high = cat.cover_image.url;
            low = cat.cover_image.lowUrl || cat.cover_image.url;
        } else if (cat.icon && typeof cat.icon === 'string' && (cat.icon.startsWith('http') || cat.icon.startsWith('/') || cat.icon.startsWith('data:'))) {
            high = cat.icon;
            low = cat.icon;
        }

        if (high) {
            return { url: high, lowUrl: low || high };
        }

        const catNameLower = (cat.name || '').toLowerCase();
        const matchedGame = games.find(g =>
            (g.category?._id && g.category._id === cat._id) ||
            g.category?.name?.toLowerCase() === catNameLower ||
            (Array.isArray(g.categories) && g.categories.some(c => (c?._id === cat._id || (c?.name || c)?.toLowerCase() === catNameLower)))
        );
        if (matchedGame) {
            const gameHigh = matchedGame.cover_image?.url || matchedGame.image || '';
            const gameLow = matchedGame.cover_image?.lowUrl || matchedGame.image || gameHigh;
            if (gameHigh || gameLow) {
                return { url: gameHigh || gameLow, lowUrl: gameLow || gameHigh };
            }
        }
        return { url: '', lowUrl: '' };
    }, [games]);

    const activeFiltersCount = useMemo(() => {
        let count = 0;
        count += selectedGenres.length;
        count += selectedPlatforms.length;
        if (priceRange[1] < 150) count += 1;
        if (minRating > 0) count += 1;
        return count;
    }, [selectedGenres, selectedPlatforms, priceRange, minRating]);

    const [isBarFixed, setIsBarFixed] = useState(false);
    const toolbarAnchorRef = useRef(null);
    const gamesContainerRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            if (toolbarAnchorRef.current) {
                const rect = toolbarAnchorRef.current.getBoundingClientRect();
                const width = window.innerWidth;
                const offset = width >= 1024 ? 86 : width >= 768 ? 70 : width >= 425 ? 80 : 70;
                const isPastAnchor = rect.top <= offset;

                let isBeforeFooter = true;
                if (gamesContainerRef.current) {
                    const gamesRect = gamesContainerRef.current.getBoundingClientRect();
                    isBeforeFooter = gamesRect.bottom > (offset + 100);
                }

                setIsBarFixed(isPastAnchor && isBeforeFooter);
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 601) {
                setViewMode('grid');
            }
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleFilterToggle = useCallback(() => {
        setShowFilters(prev => !prev);
    }, []);

    // Handle click outside and Escape key to close filter drawer and sort dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                filterBtnRef.current &&
                !filterBtnRef.current.contains(event.target)
            ) {
                setShowFilters(false);
            }
            if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
                setIsSortOpen(false);
            }
        };

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setShowFilters(false);
                setIsSortOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    // Prevent body and document scroll when filter drawer is open
    useEffect(() => {
        if (showFilters) {
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
            if (window.__lenis) {
                window.__lenis.stop();
            }
        } else {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
            if (window.__lenis) {
                window.__lenis.start();
            }
        }
        return () => {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
            if (window.__lenis) {
                window.__lenis.start();
            }
        };
    }, [showFilters]);

    // Debounce the search query dispatch to Redux store
    useEffect(() => {
        const timer = setTimeout(() => {
            dispatch(setSearchQuery(localSearchQuery));
        }, 500);
        return () => clearTimeout(timer);
    }, [localSearchQuery, dispatch]);

    // Sync local search query if Redux search query changes (e.g., filter reset)
    useEffect(() => {
        setLocalSearchQuery(searchQuery || '');
    }, [searchQuery]);

    const handleAddToCartClick = useCallback((game) => {
        const available = Object.entries(game.platforms || {}).filter(([_, info]) => info?.available);
        if (available.length === 1) {
            dispatch(addToCart({ ...game, selectedPlatform: available[0][0] }));
        } else if (available.length > 1) {
            setPlatformSelectorGame(game);
            const keys = available.map(a => a[0]);
            setSelectedPlatformForGame(keys.includes('windows') ? 'windows' : keys[0]);
        } else {
            dispatch(addToCart({ ...game, selectedPlatform: 'windows' }));
        }
    }, [dispatch]);

    const containerRef = useRef(null);


    // Read ?category= and ?platform= query param on mount/URL change and pre-select filters
    // Use a ref to prevent the URL sync effect from triggering a loop
    const isUrlDrivenUpdate = useRef(false);
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const categoryParam = params.get('category');
        const platformParam = params.get('platform');
        isUrlDrivenUpdate.current = true;

        if (categoryParam && categoryParam !== 'All') {
            const genres = categoryParam.split(',');
            dispatch(setSelectedCategory(genres[0]));
            setSelectedGenres(genres);
        } else {
            dispatch(setSelectedCategory('All'));
            setSelectedGenres([]);
        }

        if (platformParam) {
            setSelectedPlatforms(platformParam.split(','));
        } else {
            setSelectedPlatforms([]);
        }

        // Reset the flag after state updates are batched
        setTimeout(() => { isUrlDrivenUpdate.current = false; }, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Sync URL when user changes selectedGenres or selectedPlatforms from the filter sidebar
    useEffect(() => {
        if (isUrlDrivenUpdate.current) return; // skip if triggered by URL reading
        const params = new URLSearchParams();
        if (selectedGenres.length > 0) {
            params.set('category', selectedGenres.join(','));
        }
        if (selectedPlatforms.length > 0) {
            params.set('platform', selectedPlatforms.join(','));
        }
        const qs = params.toString();
        navigate(qs ? `/all-games?${qs}` : '/all-games', { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGenres, selectedPlatforms]);

    // Track previous filter criteria and initial mount
    const isFirstMount = useRef(true);
    const lastFilterRef = useRef(null);

    /** Must match fetch `limit` and be divisible by grid columns (2, 3, 4) so skeleton rows fill completely. */
    const GAMES_PAGE_LIMIT = 12;

    // Triggers whenever any filter criteria or sort option changes (resets to page 1 only if filters change)
    useEffect(() => {
        const currentPayload = {
            search: searchQuery || '',
            searchOnlyName: true,
            categories: selectedGenres.length > 0 ? selectedGenres : undefined,
            minPrice: priceRange[0],
            maxPrice: priceRange[1] < 150 ? priceRange[1] : undefined,
            minRating,
            platforms: selectedPlatforms,
            sortBy
        };

        const isSameFilter = (a, b) => {
            if (!a || !b) return false;
            return (
                (a.search || '') === (b.search || '') &&
                JSON.stringify(a.categories || []) === JSON.stringify(b.categories || []) &&
                (a.minPrice ?? 0) === (b.minPrice ?? 0) &&
                (a.maxPrice ?? 150) === (b.maxPrice ?? 150) &&
                (a.minRating ?? 0) === (b.minRating ?? 0) &&
                JSON.stringify(a.platforms || []) === JSON.stringify(b.platforms || []) &&
                (a.sortBy || 'newest') === (b.sortBy || 'newest')
            );
        };

        // If on initial mount we already have games loaded in Redux and the filters haven't changed:
        if (isFirstMount.current) {
            isFirstMount.current = false;
            lastFilterRef.current = currentPayload;

            if (games && games.length > 0) {
                setIsInitialFetched(true);
                const hasSavedScroll = Boolean(sessionStorage.getItem('cg_scroll_/all-games'));
                if (hasSavedScroll) {
                    restoreScrollPosition('/all-games');
                } else {
                    window.scrollTo(0, 0);
                    if (window.__lenis) {
                        window.__lenis.scrollTo(0, { immediate: true, force: true });
                    }
                }
                return;
            }
        }

        // Filters changed or initial empty fetch: reset to page 1 and fetch fresh
        if (!isSameFilter(lastFilterRef.current, currentPayload) || !isInitialFetched) {
            lastFilterRef.current = currentPayload;
            sessionStorage.removeItem('cg_scroll_/all-games');
            dispatch(fetchGames({
                ...currentPayload,
                page: 1,
                limit: GAMES_PAGE_LIMIT
            })).finally(() => {
                setIsInitialFetched(true);
            });
        }
    }, [searchQuery, selectedGenres, priceRange, minRating, selectedPlatforms, sortBy, dispatch, isInitialFetched]);

    // Handle Infinite scroll for fetching nextPage (guarded & passive for mobile performance)
    useEffect(() => {
        let isFetching = false;
        const handleScroll = () => {
            if (loading || isFetchingMore) return;
            const threshold = 900; // Trigger when within 900px of page bottom before footer
            const isNearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - threshold;
            if (isNearBottom && pagination && pagination.hasMore !== false && pagination.currentPage < pagination.totalPages) {
                isFetching = true;
                const nextPage = pagination.currentPage + 1;
                setIsFetchingMore(true);
                dispatch(fetchGames({
                    search: searchQuery,
                    searchOnlyName: true,
                    categories: selectedGenres.length > 0 ? selectedGenres : (selectedCategory !== 'All' ? [selectedCategory] : undefined),
                    minPrice: priceRange[0],
                    maxPrice: priceRange[1] < 150 ? priceRange[1] : undefined,
                    minRating,
                    platforms: selectedPlatforms,
                    sortBy,
                    page: nextPage,
                    limit: GAMES_PAGE_LIMIT
                })).finally(() => {
                    setIsFetchingMore(false);
                });
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [pagination, loading, isFetchingMore, searchQuery, selectedGenres, selectedCategory, priceRange, minRating, selectedPlatforms, sortBy, dispatch]);

    // Games returned from Redux store (already filtered and paginated by the API server)
    const filteredGames = games;

    useLayoutEffect(() => {
        const isReturning = Boolean(sessionStorage.getItem('cg_scroll_/all-games'));
        if (isReturning) {
            restoreScrollPosition('/all-games');
            return;
        }

        window.scrollTo(0, 0);
        if (window.__lenis) {
            window.__lenis.scrollTo(0, { immediate: true, force: true });
        }

        // Animation for page-level elements on fresh mount
        const ctx = gsap.context(() => {
            gsap.fromTo('.header-title-animate',
                { opacity: 0, y: 40 },
                { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }
            );
            gsap.fromTo('.header-search-animate',
                { opacity: 0, y: 40 },
                { opacity: 1, y: 0, duration: 0.8, delay: 0.1, ease: 'power3.out' }
            );
            gsap.fromTo('.page-animate',
                { opacity: 0, y: 30 },
                { opacity: 1, y: 0, duration: 0.8, stagger: 0.05, ease: 'power3.out' }
            );
        }, containerRef);
        return () => ctx.revert();
    }, []); // Run exactly once on page mount

    const PLATFORMS = [
        { id: 'windows', label: 'PC' },
        { id: 'playstation', label: 'PlayStation' },
        { id: 'xbox', label: 'Xbox' },
        { id: 'nintendo', label: 'Nintendo Switch' },
        { id: 'quest', label: 'Meta Quest VR' },
        { id: 'android', label: 'Android' },
        { id: 'ios', label: 'iOS' },
        { id: 'visionpro', label: 'Vision Pro' },
        { id: 'steamdeck', label: 'Steam Deck' }
    ];

    const { gridCols, setGridRef, getCornerStyle } = useGridCorners({ viewMode });

    const renderedGames = useMemo(() => {
        return filteredGames.map((game, index) => (
            viewMode === 'grid' ? (
                <GameCard
                    key={game.id || game._id}
                    game={game}
                    onAddToCart={handleAddToCartClick}
                    style={getCornerStyle(index, filteredGames.length, gridCols)}
                />
            ) : (
                <GameListCard
                    key={game.id || game._id}
                    game={game}
                    showFilters={showFilters}
                    onAddToCart={handleAddToCartClick}
                    style={getCornerStyle(index, filteredGames.length, gridCols)}
                />
            )
        ));
    }, [filteredGames, viewMode, showFilters, handleAddToCartClick, gridCols, getCornerStyle]);

    return (
        <div ref={containerRef} className="min-h-screen bg-transparent text-gray-100 pt-24 pb-12 sm:pt-32 sm:pb-32 relative overflow-hidden font-sans selection:bg-cyber-blue selection:text-black flex flex-col items-center">
            <Helmet>
                <title>All Games Catalog | CUBE GAMES</title>
                <meta name="description" content="Browse our catalog of premium digital PC, PlayStation, Xbox, and VR games. Filter by category, platform, price, or rating to find exactly what you want." />
                <meta name="keywords" content="PC games, Xbox, PlayStation, Switch, VR games, buy digital games catalog, indie games list" />
                <link rel="canonical" href="https://cubegames.ca/all-games" />
                <meta property="og:type" content="website" />
                <meta property="og:title" content="All Games Catalog | CUBE GAMES" />
                <meta property="og:description" content="Browse our catalog of premium digital PC, PlayStation, Xbox, and VR games. Filter by category, platform, price, or rating to find exactly what you want." />
                <meta property="og:url" content="https://cubegames.ca/all-games" />
            </Helmet>

            {/* Dynamic Ambient Platform Lighting */}
            <div className="absolute top-0 left-0 w-full h-full bg-cyber-grid opacity-[0.04] pointer-events-none" />

            {/* Dynamic Center Halo Ambient Light (Cyan matching FAQ page) */}
            <div
                className="absolute top-[8%] left-1/2 -translate-x-1/2 w-[70vw] max-w-[900px] h-[400px] rounded-full blur-[140px] transition-all duration-700 pointer-events-none opacity-15"
                style={{ backgroundColor: '#00f0ff' }}
            />

            <div className="absolute top-[30%] right-[-10%] w-[45vw] h-[45vw] max-w-[600px] bg-cyber-purple/10 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-[10%] left-[-10%] w-[45vw] h-[45vw] max-w-[600px] bg-cyber-blue/10 rounded-full blur-[150px] pointer-events-none" />

            <div className="container w-full relative z-10 mx-auto">
                {/* Header */}
                <div className="mb-6 sm:mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4 sm:gap-6 relative z-10">
                    <div className="flex-shrink-0 header-title-animate">
                        <p className="text-[10px] font-cyber font-semibold text-cyber-cyan uppercase tracking-[0.25em] mb-2 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse"></span>
                            Game Library
                        </p>
                        <h1 className="font-cyber font-semibold text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight whitespace-nowrap">
                            Browse <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)]">Games</span>
                        </h1>
                        <div className="h-[2px] w-20 animate-gradient-bg mt-3 rounded-full"></div>
                    </div>

                    <div className="relative group w-full md:w-72 lg:w-80 xl:w-96 flex-shrink-0 header-search-animate">
                        <div className="relative flex items-center w-full rounded-2xl bg-[#0c101b] border border-white/[0.1] transition-all duration-300 hover:border-white/20 focus-within:border-cyber-cyan focus-within:shadow-[0_0_15px_rgba(0,240,255,0.2)]">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-cyber-cyan group-focus-within:drop-shadow-[0_0_8px_rgba(0,240,255,0.6)] transition-colors duration-300 pointer-events-none" />
                            <input
                                type="text"
                                placeholder="Search games..."
                                value={localSearchQuery}
                                onChange={(e) => setLocalSearchQuery(e.target.value)}
                                className="pl-10 pr-9 py-2.5 sm:py-3 text-xs font-cyber text-white placeholder-gray-500 bg-transparent focus:outline-none w-full tracking-wider"
                            />
                            {localSearchQuery && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setLocalSearchQuery('');
                                        dispatch(setSearchQuery(''));
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                                    title="Clear search"
                                    aria-label="Clear search"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>



                {/* Top Category Artwork Carousel */}
                {categoryList && categoryList.length > 0 ? (
                    <div className="relative w-full mb-6 sm:mb-8 group/slider px-1">
                        {/* Left Scroll Arrow */}
                        <button
                            type="button"
                            onClick={() => scrollCategories('left')}
                            disabled={!canScrollCategoriesLeft}
                            className={`absolute left-0 sm:-left-3 md:-left-4 top-[52px] xs:top-[60px] sm:top-[68px] md:top-[76px] -translate-y-1/2 z-20 w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-[#080814]/90 backdrop-blur-xl border text-white flex items-center justify-center shadow-2xl transition-all duration-300 group/btn ${!canScrollCategoriesLeft
                                ? 'opacity-20 cursor-not-allowed border-white/5 pointer-events-none'
                                : 'border-white/15 hover:border-cyber-cyan/80 hover:bg-cyber-cyan/20 hover:shadow-[0_0_25px_rgba(0,240,255,0.5),0_0_10px_rgba(189,0,255,0.3)] active:scale-95 cursor-pointer'
                                }`}
                            aria-label="Previous categories"
                        >
                            <ChevronLeft className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors ${!canScrollCategoriesLeft ? 'text-gray-600' : 'text-gray-300 group-hover/btn:text-cyber-cyan'
                                }`} />
                        </button>

                        {/* Horizontal Categories Scroll */}
                        <div
                            ref={categoryScrollRef}
                            className="flex items-center gap-3 sm:gap-4 md:gap-6 overflow-x-auto scrollbar-none py-2.5 px-0 scroll-smooth select-none"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        >
                            {categoryList.map((cat) => {
                                const isSelected = selectedGenres.includes(cat.name);
                                const { url: coverImg, lowUrl: coverLowImg } = getCategoryCover(cat);
                                return (
                                    <div
                                        key={cat._id || cat.name}
                                        onClick={() => {
                                            setSelectedGenres(prev =>
                                                prev.includes(cat.name) ? prev.filter(x => x !== cat.name) : [...prev, cat.name]
                                            );
                                        }}
                                        className="flex flex-col items-center flex-shrink-0 cursor-pointer group select-none"
                                    >
                                        <div className="relative">
                                            {/* Subtle Ambient Glow */}
                                            <div className={`absolute -inset-0.5 rounded-2xl transition-opacity duration-300 pointer-events-none ${isSelected
                                                ? 'bg-cyber-cyan/20 blur-sm opacity-100'
                                                : 'bg-cyber-cyan/10 blur-xs opacity-0 group-hover:opacity-60'
                                                }`} />

                                            {/* Card Border & Container */}
                                            <div className={`relative p-[2px] rounded-2xl transition-all duration-300 ${isSelected
                                                ? 'bg-gradient-to-br from-cyber-cyan via-[#00f0ff] to-cyber-purple shadow-[0_0_12px_rgba(0,240,255,0.35)]'
                                                : 'bg-white/10 group-hover:bg-gradient-to-br group-hover:from-cyber-cyan/50 group-hover:to-cyber-purple/50 group-hover:shadow-[0_0_8px_rgba(0,240,255,0.2)]'
                                                }`}>
                                                <div className="relative w-20 h-20 xs:w-24 xs:h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-[14px] overflow-hidden bg-[#0a0a14]">
                                                    {coverImg ? (
                                                        <ProgressiveImage
                                                            src={coverImg}
                                                            placeholderSrc={coverLowImg || coverImg}
                                                            alt={cat.name}
                                                            className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#121124] to-[#0a0a16] p-2 text-center">
                                                            <span className="text-xl sm:text-2xl font-cyber font-bold text-cyber-cyan/70">
                                                                {cat.name?.charAt(0) || 'G'}
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent opacity-80 group-hover:opacity-20 transition-opacity" />
                                                    {isSelected && (
                                                        <>
                                                            <div className="absolute inset-0 bg-cyber-cyan/10 pointer-events-none" />
                                                            <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-gradient-to-br from-cyber-cyan to-cyber-blue text-black flex items-center justify-center shadow-[0_0_6px_rgba(0,240,255,0.5)]">
                                                                <Check className="w-2.5 h-2.5 stroke-[3]" />
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <span className={`text-[10px] xs:text-[11px] sm:text-xs font-cyber font-bold uppercase tracking-wider text-center mt-2.5 transition-all truncate max-w-[85px] xs:max-w-[100px] sm:max-w-[120px] md:max-w-none ${isSelected ? 'text-cyber-cyan font-bold drop-shadow-[0_0_6px_rgba(0,240,255,0.5)]' : 'text-gray-300 group-hover:text-white'
                                            }`}>
                                            {cat.name}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Right Scroll Arrow */}
                        <button
                            type="button"
                            onClick={() => scrollCategories('right')}
                            disabled={!canScrollCategoriesRight}
                            className={`absolute right-0 sm:-right-3 md:-right-4 top-[52px] xs:top-[60px] sm:top-[68px] md:top-[76px] -translate-y-1/2 z-20 w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-[#080814]/90 backdrop-blur-xl border text-white flex items-center justify-center shadow-2xl transition-all duration-300 group/btn ${!canScrollCategoriesRight
                                ? 'opacity-20 cursor-not-allowed border-white/5 pointer-events-none'
                                : 'border-white/15 hover:border-cyber-cyan/80 hover:bg-cyber-cyan/20 hover:shadow-[0_0_25px_rgba(0,240,255,0.5),0_0_10px_rgba(189,0,255,0.3)] active:scale-95 cursor-pointer'
                                }`}
                            aria-label="Next categories"
                        >
                            <ChevronRight className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors ${!canScrollCategoriesRight ? 'text-gray-600' : 'text-gray-300 group-hover/btn:text-cyber-cyan'
                                }`} />
                        </button>
                    </div>
                ) : (!isInitialFetched || categoryLoading || loading || !categories || categories.length === 0) ? (
                    <div className="relative w-full mb-6 sm:mb-8 px-1">
                        <div
                            className="flex items-center gap-3 sm:gap-4 md:gap-6 overflow-x-hidden py-2.5 px-0 select-none"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        >
                            {[...Array(14)].map((_, i) => (
                                <CategorySkeleton key={i} />
                            ))}
                        </div>
                    </div>
                ) : null}

                {/* Fixed / Sticky Toolbar Anchor */}
                <div ref={toolbarAnchorRef} className={`w-full relative z-30 ${isBarFixed ? 'h-[62px] sm:h-[70px] md600:h-[78px] mb-6' : 'mb-6'}`}>
                    <div className={`transition-all duration-300 ${isBarFixed ? 'fixed top-[70px] sm:top-[80px] md:top-[70px]  lg:top-[86px] left-0 right-0 w-full z-30' : 'w-full relative z-30'}`}>
                        <div className={`w-full mx-auto  ${isBarFixed ? 'container' : ''}`}>
                            <div className="p-2.5 sm:px-4 md600:px-5 py-2.5 md600:py-3.5  bg-[#0c101b] border border-white/[0.1] shadow-[0_10px_30px_rgba(0,0,0,0.6)] transition-all duration-300 relative z-30">
                                {/* Row 1: Left Controls (Sort By) & Right Controls (View Switcher + Filters) */}
                                <div className="flex items-center justify-between gap-2 md600:gap-4 w-full">
                                    {/* Left: Sort By Dropdown (takes content width only) */}
                                    <div className="relative flex-shrink-0 z-40" ref={sortDropdownRef}>
                                        <button
                                            type="button"
                                            onClick={() => setIsSortOpen(prev => !prev)}
                                            className={`w-auto max-w-full min-w-0 flex items-center justify-between gap-1.5 sm:gap-2.5 px-2.5 sm:px-4 py-2 sm:py-2.5  bg-[#151a26] border text-[10px] xs:text-[11px] sm:text-xs font-cyber font-bold uppercase tracking-wider transition-all duration-300 shadow-sm ${isSortOpen
                                                ? 'border-cyber-cyan text-cyber-cyan bg-cyber-cyan/15 shadow-[0_0_15px_rgba(0,240,255,0.25)]'
                                                : 'border-white/[0.08] text-gray-200 hover:text-white hover:border-white/20 hover:bg-[#1a2130]'
                                                }`}
                                        >
                                            <span className="flex items-center gap-1.5 min-w-0">
                                                <span className="text-gray-400 font-semibold hidden md:inline flex-shrink-0">SORT BY :</span>
                                                <span className="text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.2)] truncate block max-w-[78px] xs:max-w-[100px] sm375:max-w-[125px] sm:max-w-[170px] md:max-w-none" title={currentSortLabel}>
                                                    {currentSortLabel}
                                                </span>
                                            </span>
                                            <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 text-gray-400 transition-transform duration-300 ${isSortOpen ? 'rotate-180 text-cyber-cyan' : ''}`} />
                                        </button>

                                        {isSortOpen && (
                                            <div className="absolute left-0 top-full mt-2 w-48 sm:w-60 py-1.5 rounded-xl bg-[#0f1422] border border-white/10 shadow-[0_15px_35px_rgba(0,0,0,0.8)] z-50 overflow-hidden">
                                                {SORT_OPTIONS.map((opt) => (
                                                    <button
                                                        key={opt.value}
                                                        type="button"
                                                        onClick={() => {
                                                            setSortBy(opt.value);
                                                            setIsSortOpen(false);
                                                        }}
                                                        className={`w-full flex items-center justify-between px-3 sm:px-4 py-2 sm:py-2.5 text-[10px] xs:text-[11px] sm:text-xs font-cyber font-semibold tracking-wider text-left transition-colors ${sortBy === opt.value
                                                            ? 'bg-cyber-cyan/15 text-cyber-cyan font-bold'
                                                            : 'text-gray-300 hover:bg-white/[0.06] hover:text-white'
                                                            }`}
                                                    >
                                                        <span>{opt.label}</span>
                                                        {sortBy === opt.value && <Check className="w-3.5 h-3.5 text-cyber-cyan" />}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Desktop Center: Games Found Count (Visible >= md 768px) */}
                                    <div className="hidden md:flex items-center justify-center text-center flex-1 min-w-0 px-2">
                                        {/* {(!loading || filteredGames.length > 0) && (
                                            <span className="font-cyber font-bold text-xs md:text-sm text-gray-300 tracking-[0.16em] uppercase truncate">
                                                {(pagination?.totalItems || filteredGames.length).toLocaleString()} GAMES FOUND
                                            </span>
                                        )} */}
                                    </div>

                                    {/* Right: View Switcher (Grid & List View) & Filters Toggle */}
                                    <div className="flex items-center gap-1.5 sm:gap-3 justify-end flex-shrink-0">
                                        <div className="hidden md600:flex items-center gap-0.5 sm:gap-1 p-0.5 sm:p-1 bg-[#151a26] border border-white/[0.08]">
                                            <button
                                                type="button"
                                                onClick={() => setViewMode('grid')}
                                                className={`p-1.5 sm:p-2 transition-all duration-300 ${viewMode === 'grid'
                                                    ? 'bg-white text-black shadow-md shadow-white/10'
                                                    : 'text-gray-400 hover:text-white'
                                                    }`}
                                                title="Grid View"
                                                aria-label="Grid View"
                                            >
                                                <Grid className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setViewMode('list')}
                                                className={`p-1.5 sm:p-2 transition-all duration-300 ${viewMode === 'list'
                                                    ? 'bg-white text-black shadow-md shadow-white/10'
                                                    : 'text-gray-400 hover:text-white'
                                                    }`}
                                                title="List View"
                                                aria-label="List View"
                                            >
                                                <List className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                            </button>
                                        </div>

                                        <button
                                            ref={filterBtnRef}
                                            type="button"
                                            onClick={handleFilterToggle}
                                            className={`relative flex items-center justify-center gap-1.5 sm:gap-2 px-2.5 sm:px-5 py-2 sm:py-2.5 rounded-sm text-[10px] xs:text-[11px] sm:text-xs font-cyber font-bold tracking-wider transition-all duration-300 border uppercase flex-shrink-0
                                                ${showFilters
                                                    ? 'bg-cyber-cyan/15 border-cyber-cyan text-cyber-cyan shadow-[0_0_20px_rgba(0,240,255,0.25)]'
                                                    : 'bg-[#151a26] border-white/[0.08] text-gray-200 hover:text-white hover:border-white/20 hover:bg-[#1a2130]'}`}
                                        >
                                            <SlidersHorizontal className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                            <span>FILTERS</span>
                                            {activeFiltersCount > 0 && (
                                                <span className="flex items-center justify-center bg-cyber-pink text-white text-[9px] font-bold w-[17px] h-[17px] sm:w-[18px] sm:h-[18px] rounded-full shadow-[0_0_8px_rgba(255,0,122,0.8)] ml-0.5">
                                                    {activeFiltersCount}
                                                </span>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Row 2: Mobile/Tablet Games Found Count (Visible below md 768px) */}
                                {/* {(!loading || filteredGames.length > 0) && (
                                    <div className="md:hidden pt-2 mt-2 border-t border-white/[0.07] text-center">
                                        <span className="font-cyber font-bold text-[10px] xs:text-[11px] text-gray-300 tracking-[0.14em] uppercase inline-flex items-center justify-center gap-1.5 flex-wrap">
                                            <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse"></span>
                                            <span>{(pagination?.totalItems || filteredGames.length).toLocaleString()} GAMES FOUND</span>
                                        </span>
                                    </div>
                                )} */}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Off-Canvas Filter Drawer (Mounted to document.body via Portal) */}
                {createPortal(
                    <div data-lenis-prevent className={`fixed inset-0 z-[9999] overflow-hidden transition-all duration-300 ${showFilters ? 'pointer-events-auto visible opacity-100' : 'pointer-events-none invisible opacity-0'}`}>
                        {/* 1. Full-Height Dark Backdrop */}
                        <div
                            onClick={() => setShowFilters(false)}
                            className={`fixed inset-0 bg-black/75 transition-opacity duration-300 ${showFilters ? 'opacity-100' : 'opacity-0'
                                }`}
                            aria-hidden="true"
                        />

                        {/* 2. Full-Height Slide-over Panel (Full width below 425px, 420px on sm/desktop) */}
                        <div
                            ref={dropdownRef}
                            className={`fixed top-0 right-0 bottom-0 h-full w-full sm:w-[420px] sm:max-w-[420px] bg-[#070b14] border-l border-white/10 flex flex-col transition-all duration-300 ease-in-out ${showFilters ? 'translate-x-0 shadow-[-15px_0_30px_rgba(0,0,0,0.8)]' : 'translate-x-full shadow-none'
                                }`}
                            role="dialog"
                            aria-modal="true"
                            aria-label="Filter games drawer"
                        >
                            {/* Glowing Cyber Accent Line */}
                            <div className="h-[2px] w-full bg-gradient-to-r from-cyber-cyan via-[#bd00ff] to-cyber-blue flex-shrink-0" />

                            {/* Drawer Header */}
                            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.08] bg-[#0b0f1c] flex-shrink-0">
                                <div className="flex items-center gap-2.5">
                                    <span className="w-2 h-2 rounded-full bg-cyber-cyan shadow-[0_0_8px_#00f0ff] animate-pulse" />
                                    <h3 className="font-cyber font-bold text-sm sm:text-base text-white uppercase tracking-wider">
                                        FILTERS
                                    </h3>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowFilters(false)}
                                    className="p-2 rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:border-cyber-cyan/50 hover:bg-cyber-cyan/10 transition-all duration-300 active:scale-95"
                                    aria-label="Close filters"
                                >
                                    <X className="w-4 h-4 sm:w-5 sm:h-5" />
                                </button>
                            </div>

                            {/* Scrollable Drawer Body */}
                            <div
                                data-lenis-prevent
                                className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4 scrollbar-thin"
                                style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(0, 240, 255, 0.2) transparent' }}
                            >
                                {/* 1. Categories Section Card */}
                                <div className="p-4 rounded-2xl bg-[#0c1120] border border-white/[0.08] space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-cyber-purple shadow-[0_0_8px_#bd00ff]"></span>
                                            <span className="text-[11px] font-cyber font-bold text-gray-200 uppercase tracking-wider">Categories</span>
                                        </div>
                                        {selectedGenres.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => setSelectedGenres([])}
                                                className="text-[10px] font-cyber font-bold text-cyber-purple hover:text-white px-2 py-0.5 rounded-lg bg-cyber-purple/15 hover:bg-cyber-purple/30 border border-cyber-purple/40 uppercase tracking-wider transition-all"
                                            >
                                                Clear ({selectedGenres.length})
                                            </button>
                                        )}
                                    </div>
                                    <div
                                        data-lenis-prevent
                                        className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1"
                                        style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(189, 0, 255, 0.25) transparent' }}
                                    >
                                        {[...categories].sort((a, b) => {
                                            const aSelected = selectedGenres.includes(a.name);
                                            const bSelected = selectedGenres.includes(b.name);
                                            if (aSelected === bSelected) return 0;
                                            return aSelected ? -1 : 1;
                                        }).map((c) => {
                                            const isSelected = selectedGenres.includes(c.name);
                                            return (
                                                <button
                                                    key={c._id}
                                                    type="button"
                                                    onClick={() => setSelectedGenres(prev => prev.includes(c.name) ? prev.filter(x => x !== c.name) : [...prev, c.name])}
                                                    className={`flex items-center justify-between px-3 py-2 rounded-xl text-[10px] font-cyber font-medium uppercase transition-all duration-200 border ${isSelected
                                                        ? 'bg-cyber-purple/20 border-cyber-purple text-white shadow-[0_0_12px_rgba(189,0,255,0.3)] font-bold'
                                                        : 'bg-white/[0.02] border-white/[0.06] hover:border-cyber-purple/40 hover:bg-cyber-purple/5 text-gray-400 hover:text-gray-200'
                                                        }`}
                                                >
                                                    <span className="truncate text-left">{c.name}</span>
                                                    {isSelected ? (
                                                        <Check className="w-3 h-3 flex-shrink-0 text-cyber-purple stroke-[3]" />
                                                    ) : (
                                                        <span className="text-[10px] text-gray-600 font-light">＋</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* 2. Platforms Section Card */}
                                <div className="p-4 rounded-2xl bg-[#0c1120] border border-white/[0.08] space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-cyber-blue shadow-[0_0_8px_#00ffff]"></span>
                                            <span className="text-[11px] font-cyber font-bold text-gray-200 uppercase tracking-wider">Platforms</span>
                                        </div>
                                        {selectedPlatforms.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => setSelectedPlatforms([])}
                                                className="text-[10px] font-cyber font-bold text-cyber-blue hover:text-white px-2 py-0.5 rounded-lg bg-cyber-blue/15 hover:bg-cyber-blue/30 border border-cyber-blue/40 uppercase tracking-wider transition-all"
                                            >
                                                Clear ({selectedPlatforms.length})
                                            </button>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        {[...PLATFORMS].sort((a, b) => {
                                            const aSelected = selectedPlatforms.includes(a.id);
                                            const bSelected = selectedPlatforms.includes(b.id);
                                            if (aSelected === bSelected) return 0;
                                            return aSelected ? -1 : 1;
                                        }).map((p) => {
                                            const isSelected = selectedPlatforms.includes(p.id);
                                            return (
                                                <button
                                                    key={p.id}
                                                    type="button"
                                                    onClick={() => setSelectedPlatforms(prev => prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id])}
                                                    className={`flex items-center justify-between px-3 py-2 rounded-xl text-[10px] font-cyber font-medium uppercase transition-all duration-200 border ${isSelected
                                                        ? 'bg-cyber-blue/20 border-cyber-blue text-white shadow-[0_0_12px_rgba(0,240,255,0.3)] font-bold'
                                                        : 'bg-white/[0.02] border-white/[0.06] hover:border-cyber-blue/40 hover:bg-cyber-blue/5 text-gray-400 hover:text-gray-200'
                                                        }`}
                                                >
                                                    <span className="truncate text-left">{p.label}</span>
                                                    {isSelected ? (
                                                        <Check className="w-3 h-3 flex-shrink-0 text-cyber-blue stroke-[3]" />
                                                    ) : (
                                                        <span className="text-[10px] text-gray-600 font-light">＋</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* 3. Price Range Section Card */}
                                <div className="p-4 rounded-2xl bg-[#0c1120] border border-white/[0.08] space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-cyber-pink shadow-[0_0_8px_#ff007a]"></span>
                                            <span className="text-[11px] font-cyber font-bold text-gray-200 uppercase tracking-wider">Max Price</span>
                                        </div>
                                        <span className="font-mono text-xs font-bold px-2.5 py-1 rounded-lg bg-cyber-pink/15 border border-cyber-pink/40 text-cyber-pink shadow-[0_0_10px_rgba(255,0,122,0.2)]">
                                            {priceRange[1] === 0 ? '$0 (Free)' : `$0 - $${priceRange[1] === 150 ? '150+' : priceRange[1]}`}
                                        </span>
                                    </div>
                                    <div className="space-y-2 pt-1.5">
                                        <input
                                            type="range"
                                            min="0"
                                            max="150"
                                            step="5"
                                            value={priceRange[1]}
                                            data-lenis-prevent
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onTouchStart={(e) => e.stopPropagation()}
                                            onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value, 10)])}
                                            style={{
                                                background: `linear-gradient(to right, #ff007a 0%, #ff007a ${Math.min(100, Math.max(0, (priceRange[1] / 150) * 100))}%, rgba(255, 255, 255, 0.12) ${Math.min(100, Math.max(0, (priceRange[1] / 150) * 100))}%, rgba(255, 255, 255, 0.12) 100%)`
                                            }}
                                            className="cyber-range-slider"
                                        />
                                        <div className="flex justify-between items-center text-[10px] font-mono font-medium text-gray-400 px-0.5">
                                            <span className={priceRange[1] === 0 ? 'text-cyber-pink font-bold' : ''}>$0</span>
                                            <span className={priceRange[1] === 50 ? 'text-cyber-pink font-bold' : ''}>$50</span>
                                            <span className={priceRange[1] === 100 ? 'text-cyber-pink font-bold' : ''}>$100</span>
                                            <span className={priceRange[1] === 150 ? 'text-cyber-pink font-bold' : ''}>$150+</span>
                                        </div>
                                    </div>
                                </div>

                                {/* 4. Rating Section Card */}
                                <div className="p-4 rounded-2xl bg-[#0c1120] border border-white/[0.08] space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 shadow-[0_0_8px_#facc15]"></span>
                                            <span className="text-[11px] font-cyber font-bold text-gray-200 uppercase tracking-wider">Minimum Rating</span>
                                        </div>
                                        {minRating > 0 ? (
                                            <span className="text-[9px] font-cyber font-bold text-yellow-400 bg-yellow-400/15 border border-yellow-400/30 px-2 py-0.5 rounded-md">
                                                {minRating}+ STARS
                                            </span>
                                        ) : (
                                            <span className="text-[9px] font-cyber text-gray-500 uppercase">ANY</span>
                                        )}
                                    </div>
                                    <div className="flex items-center justify-around py-1 px-2 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                                        {[1, 2, 3, 4, 5].map((stars) => (
                                            <button
                                                key={stars}
                                                type="button"
                                                onClick={() => setMinRating(minRating === stars ? 0 : stars)}
                                                className="p-1.5 focus:outline-none hover:scale-125 active:scale-95 transition-transform duration-200"
                                                aria-label={`Rate ${stars} stars or more`}
                                            >
                                                <Star className={`w-5 h-5 transition-all duration-300 ${stars <= minRating ? 'fill-yellow-400 text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.8)]' : 'text-gray-700 hover:text-yellow-400/50'}`} />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Drawer Footer: RESET and VIEW buttons */}
                            <div className="p-4 sm:p-5 border-t border-white/[0.08] bg-[#0b0f1c] flex items-center gap-3 flex-shrink-0">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedGenres([]);
                                        setSelectedPlatforms([]);
                                        setSelectedYears([]);
                                        setPriceRange([0, 150]);
                                        setMinRating(0);
                                    }}
                                    className="flex-1 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-gray-300 hover:text-white font-cyber font-bold text-xs uppercase tracking-wider transition-all duration-200 active:scale-95 text-center"
                                >
                                    RESET
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowFilters(false)}
                                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-cyber-cyan via-[#00f0ff] to-cyber-blue hover:brightness-110 text-black font-cyber font-black text-xs uppercase tracking-wider shadow-[0_0_20px_rgba(0,240,255,0.4)] transition-all duration-200 active:scale-95 text-center"
                                >
                                    VIEW
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

                <div ref={gamesContainerRef} className="w-full relative">
                    {/* Content */}
                    <div className="w-full relative z-10">
                        {!isInitialFetched || (loading && filteredGames.length === 0) ? (
                            <div
                                ref={setGridRef}
                                className={viewMode === 'grid'
                                    ? 'grid grid-cols-1 sm500:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 w-full'
                                    : 'grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 w-full'}>
                                {[...Array(GAMES_PAGE_LIMIT)].map((_, i) => (
                                    viewMode === 'grid' ? (
                                        <GameCardSkeleton
                                            key={i}
                                            style={getCornerStyle(i, GAMES_PAGE_LIMIT, gridCols)}
                                        />
                                    ) : (
                                        <GameListCardSkeleton
                                            key={i}
                                            style={getCornerStyle(i, GAMES_PAGE_LIMIT, gridCols)}
                                        />
                                    )
                                ))}
                            </div>
                        ) : filteredGames.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-32 text-center">
                                <Search className="w-12 h-12 text-gray-700 mb-4 animate-pulse" />
                                <h3 className="text-xl font-cyber font-semibold text-white uppercase mb-2">No games found</h3>
                                <p className="text-gray-500 font-cyber text-xs uppercase tracking-widest">Try adjusting your filters or search.</p>
                            </div>
                        ) : (
                            <>
                                <div
                                    ref={setGridRef}
                                    className={viewMode === 'grid'
                                        ? 'grid grid-cols-1 sm500:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 w-full'
                                        : 'grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 w-full'}>
                                    {renderedGames}
                                </div>

                                {/* Scroll loading indicator (Only show while actively fetching next page via scroll) */}
                                {isFetchingMore && (
                                    <div className="flex justify-center mt-12 mb-6">
                                        <div className="flex items-center gap-2 px-6 py-3 rounded-full bg-white/[0.02] border border-white/5 text-cyber-cyan font-cyber animate-pulse">
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            <span>LOADING MORE GAMES...</span>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Platform Selector Modal */}
            {platformSelectorGame && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in">
                    <div className="w-full max-w-md bg-[#0c0c18] border border-cyber-cyan/30 rounded-[1.5rem] sm:rounded-[2.5rem] p-5 sm:p-8 shadow-[0_0_30px_rgba(0,240,255,0.15)] relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyber-cyan via-cyber-purple to-cyber-pink"></div>

                        <div className="space-y-6">
                            <div className="text-center space-y-2">
                                <h3 className="text-xl font-cyber font-black text-white uppercase tracking-tight">Select Platform</h3>
                                <p className="text-gray-400 text-xs font-medium">Choose your target platform for <span className="text-cyber-cyan">{platformSelectorGame.title}</span></p>
                            </div>

                            <div className="space-y-2.5 max-h-60 overflow-y-auto pr-1">
                                {Object.entries(platformSelectorGame.platforms || {})
                                    .filter(([_, info]) => info?.available)
                                    .map(([key, info]) => {
                                        const isSelected = selectedPlatformForGame === key;
                                        const Icon = PlatformIcon[key] || FaWindows;
                                        const label = PlatformLabel[key] || key;
                                        const brandColor = PlatformColor[key] || '#9ca3af';
                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                onClick={() => setSelectedPlatformForGame(key)}
                                                className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all text-left
                                                    ${isSelected
                                                        ? 'bg-cyber-cyan/10 border-cyber-cyan text-white shadow-[0_0_15px_rgba(0,240,255,0.1)]'
                                                        : 'bg-white/[0.02] border-white/5 text-gray-400 hover:text-white hover:bg-white/[0.04]'}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Icon className="w-4 h-4" style={{ color: brandColor }} />
                                                    <span className="text-xs font-cyber font-bold uppercase tracking-wide">{label}</span>
                                                </div>
                                                <span className="text-xs font-mono font-bold text-white">
                                                    {info.price === 0 ? 'FREE' : `$${info.price}`}
                                                </span>
                                            </button>
                                        );
                                    })}
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={() => { setPlatformSelectorGame(null); setSelectedPlatformForGame(''); }}
                                    className="flex-1 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-gray-400 font-cyber font-bold text-[10px] uppercase tracking-widest hover:bg-white/10 hover:text-white transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        dispatch(addToCart({ ...platformSelectorGame, selectedPlatform: selectedPlatformForGame }));
                                        setPlatformSelectorGame(null);
                                        setSelectedPlatformForGame('');
                                    }}
                                    disabled={!selectedPlatformForGame}
                                    className="flex-1 py-3.5 rounded-2xl bg-cyber-cyan/20 border border-cyber-cyan/40 text-cyber-cyan font-cyber font-bold text-[10px] uppercase tracking-widest hover:bg-cyber-cyan/30 disabled:opacity-40 transition-all"
                                >
                                    Add to Cart
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
